import {
  DAY_START_HOUR,
  DAY_END_HOUR,
  HOUR_HEIGHT_PX,
  TOTAL_GRID_HEIGHT_PX,
} from "./time-constants";

export function timeToMinutes(time: string): number {
  const [hours, minutes] = time.split(":").map(Number);
  return (hours ?? 0) * 60 + (minutes ?? 0);
}

export function getEventPosition(
  startTime: string,
  endTime: string,
): { topPx: number; heightPx: number } {
  const startMinutes = timeToMinutes(startTime);
  const endMinutes = timeToMinutes(endTime);
  const gridStartMinutes = DAY_START_HOUR * 60;
  const gridEndMinutes = DAY_END_HOUR * 60;

  const clampedStart = Math.max(startMinutes, gridStartMinutes);
  const clampedEnd = Math.min(endMinutes, gridEndMinutes);
  const duration = Math.max(clampedEnd - clampedStart, 0);

  const topPx = ((clampedStart - gridStartMinutes) * HOUR_HEIGHT_PX) / 60;
  const heightPx = (duration * HOUR_HEIGHT_PX) / 60;

  return {
    topPx: Math.max(topPx, 0),
    heightPx: Math.min(heightPx, TOTAL_GRID_HEIGHT_PX - topPx),
  };
}

export function getNowIndicatorOffset(now: Date): number | null {
  const hours = now.getHours();
  const minutes = now.getMinutes();
  const totalMinutes = hours * 60 + minutes;
  const gridStartMinutes = DAY_START_HOUR * 60;
  const gridEndMinutes = DAY_END_HOUR * 60;

  if (totalMinutes < gridStartMinutes || totalMinutes >= gridEndMinutes) {
    return null;
  }

  return ((totalMinutes - gridStartMinutes) * HOUR_HEIGHT_PX) / 60;
}

const NOW_SNAP_THRESHOLD = 7; // minutes

function snapMinutesWithNow(raw: number): number {
  const now = new Date();
  const nowM = now.getHours() * 60 + now.getMinutes();
  if (nowM >= DAY_START_HOUR * 60 && nowM < DAY_END_HOUR * 60 && Math.abs(raw - nowM) <= NOW_SNAP_THRESHOLD) {
    return nowM;
  }
  return Math.round(raw / 15) * 15;
}

export function pxToSnappedPx(pxFromTop: number, freeSnap = false): number {
  const totalMinutes = DAY_START_HOUR * 60 + (pxFromTop / HOUR_HEIGHT_PX) * 60;
  const snapped = freeSnap ? Math.round(totalMinutes) : snapMinutesWithNow(totalMinutes);
  return ((snapped - DAY_START_HOUR * 60) * HOUR_HEIGHT_PX) / 60;
}

export function pxToTime(pxFromTop: number, freeSnap = false): string {
  const totalMinutes = DAY_START_HOUR * 60 + (pxFromTop / HOUR_HEIGHT_PX) * 60;
  const snapped = freeSnap ? Math.round(totalMinutes) : snapMinutesWithNow(totalMinutes);
  const hours = Math.floor(snapped / 60);
  const minutes = snapped % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

interface OverlapEvent {
  readonly id: string;
  readonly startTime: string;
  readonly endTime: string;
}

export function computeOverlapColumns(
  events: ReadonlyArray<OverlapEvent>,
): Map<string, { column: number; totalColumns: number }> {
  const result = new Map<string, { column: number; totalColumns: number }>();

  if (events.length === 0) return result;

  const sorted = [...events].sort((a, b) => {
    const diff = timeToMinutes(a.startTime) - timeToMinutes(b.startTime);
    if (diff !== 0) return diff;
    return timeToMinutes(a.endTime) - timeToMinutes(b.endTime);
  });

  const mergedGroups: OverlapEvent[][] = [];

  for (const event of sorted) {
    let addedToGroup = false;

    for (const group of mergedGroups) {
      const overlapsAny = group.some(
        (g) =>
          timeToMinutes(event.startTime) < timeToMinutes(g.endTime) &&
          timeToMinutes(event.endTime) > timeToMinutes(g.startTime),
      );
      if (overlapsAny) {
        group.push(event);
        addedToGroup = true;
        break;
      }
    }

    if (!addedToGroup) {
      mergedGroups.push([event]);
    }
  }

  for (const group of mergedGroups) {
    const columns: OverlapEvent[][] = [];

    for (const event of group) {
      let placedInColumn = false;

      for (let col = 0; col < columns.length; col++) {
        const column = columns[col];
        if (!column) continue;
        const hasConflict = column.some(
          (e) =>
            timeToMinutes(event.startTime) < timeToMinutes(e.endTime) &&
            timeToMinutes(event.endTime) > timeToMinutes(e.startTime),
        );
        if (!hasConflict) {
          column.push(event);
          result.set(event.id, { column: col, totalColumns: 0 });
          placedInColumn = true;
          break;
        }
      }

      if (!placedInColumn) {
        columns.push([event]);
        result.set(event.id, { column: columns.length - 1, totalColumns: 0 });
      }
    }

    const totalColumns = columns.length;
    for (const event of group) {
      const entry = result.get(event.id);
      if (entry) {
        entry.totalColumns = totalColumns;
      }
    }
  }

  return result;
}
